/**
 * Which tabs a tag's info card has: the one list, by the card the tag opens in
 * (`ForgeBaseTag.getInfoComponent()`, forgeModels.js). Anything that opens a card on a tab (the
 * assistant's `ui.tags.open_panel`, a button that jumps to Documents or Tasks) reads it here instead
 * of knowing per kind. `__tests__/tagInfoTabs.test.js` checks every card's template against this list,
 * so a tab added to a card without adding it here (or the other way round) fails the tests.
 *
 * A tab's value on the card is TAG_INFO_TAB (constants/tabs.js): the cards pin the later tabs with
 * `tab-value` and leave Info, Documents, Tasks and Monitoring in that order first.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { TAG_INFO_TAB } from '../../../constants/tabs.js';

/** A card tab by name: the names the assistant and links use (`tab=documents`). */
export const TAG_TAB = Object.freeze({
  INFO: 'info',
  DOCUMENTS: 'documents',
  TASKS: 'tasks',
  MONITORING: 'monitoring',
  RECORDS: 'records',
  FINDINGS: 'findings',
  SNAPS: 'snaps',
});

/** A tab name → the card's tab value. */
export const TAG_TAB_VALUE = Object.freeze({
  [TAG_TAB.INFO]: TAG_INFO_TAB.INFO,
  [TAG_TAB.DOCUMENTS]: TAG_INFO_TAB.DOCUMENTS,
  [TAG_TAB.TASKS]: TAG_INFO_TAB.TASKS,
  [TAG_TAB.MONITORING]: TAG_INFO_TAB.MONITORING,
  [TAG_TAB.RECORDS]: TAG_INFO_TAB.RECORDS,
  [TAG_TAB.FINDINGS]: TAG_INFO_TAB.FINDINGS,
  [TAG_TAB.SNAPS]: TAG_INFO_TAB.SNAPS,
});

/** A tab's name as the card shows it (its tooltip), for plain words. */
export const TAG_TAB_LABEL = Object.freeze({
  [TAG_TAB.INFO]: 'Info',
  [TAG_TAB.DOCUMENTS]: 'Documents',
  [TAG_TAB.TASKS]: 'Tasks',
  [TAG_TAB.MONITORING]: 'Monitoring',
  [TAG_TAB.RECORDS]: 'Records',
  [TAG_TAB.FINDINGS]: 'Findings',
  [TAG_TAB.SNAPS]: 'Snaps',
});

const EVERY_CARD = Object.freeze([TAG_TAB.INFO, TAG_TAB.DOCUMENTS, TAG_TAB.TASKS]);

/** Each card's tabs, in screen order, by the card's component name. */
export const TAG_INFO_TABS = Object.freeze({
  EquipmentInfo: Object.freeze([...EVERY_CARD, TAG_TAB.MONITORING, TAG_TAB.RECORDS]),
  PipelineInfo: Object.freeze([...EVERY_CARD, TAG_TAB.MONITORING, TAG_TAB.RECORDS]),
  StructuralInfo: Object.freeze([...EVERY_CARD, TAG_TAB.MONITORING, TAG_TAB.RECORDS]),
  InspectionInfo: Object.freeze([...EVERY_CARD, TAG_TAB.MONITORING, TAG_TAB.RECORDS]),
  AnomalyInfo: Object.freeze([...EVERY_CARD, TAG_TAB.RECORDS]),
  AnnotationInfo: Object.freeze([...EVERY_CARD, TAG_TAB.RECORDS]),
  ScaffoldInfo: Object.freeze([...EVERY_CARD, TAG_TAB.RECORDS]),
  AvaInspectInfo: Object.freeze([...EVERY_CARD, TAG_TAB.FINDINGS, TAG_TAB.RECORDS, TAG_TAB.SNAPS]),
});

/** Every tab name a card can have. */
export const ALL_TAG_TABS = Object.freeze(Object.values(TAG_TAB));

/** The tabs of the card `tag` opens in; a tag with no known card has Info only. */
export function tabsOfTag(tag) {
  const card = tag && typeof tag.getInfoComponent === 'function' ? tag.getInfoComponent() : null;
  return Object.prototype.hasOwnProperty.call(TAG_INFO_TABS, card) ? TAG_INFO_TABS[card] : [TAG_TAB.INFO];
}

/** True when the card `tag` opens in has the tab named `tab`. */
export const tagHasTab = (tag, tab) => tabsOfTag(tag).includes(tab);
